import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import sumBy from 'lodash/sumBy';
import Box from '@material-ui/core/Box';
import Typography from "@material-ui/core/Typography";
import Alert from '@material-ui/lab/Alert';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import { Query, useMutation } from 'react-apollo';
import Spin from 'components/common/Spin';
import {
  GET_USER_INVOICES,
  GET_USER_PAYMENT_ITEMS,
  GET_PAYMENT_ACCOUNT,
  DELETE_PAYMENT_ITEM,
  UPDATE_PAYMENT_ITEMS
} from 'graphql/payments';
import { getFullName } from 'utils';
import LogTime  from './components/LogTime';
import Tab from "@material-ui/core/Tab";
import {Link, Route, Switch} from "react-router-dom";
import Tabs from "@material-ui/core/Tabs";
import MaterialTable from "material-table";
import useSnack from "hooks/useSnack";
import InvoiceDetails from "./components/InvoiceDetails";
import BonusLogs from './components/BonusLogs';

const TimeLogs = () => {
  const user = useSelector((s) => s.user.user);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleteItem] = useMutation(DELETE_PAYMENT_ITEM);
  const [updateItems] = useMutation(UPDATE_PAYMENT_ITEMS);
  const snack = useSnack();

  return (
    <Box>
      <Query query={GET_USER_PAYMENT_ITEMS} variables={{ userId: user.id }} fetchPolicy={"cache-and-network"}>
        {({ data, loading, refetch }) => {
          if (loading) return <Spin spinning />;

          const onDelete = async (row) => {
            if (window.confirm('Are you sure?')) {
              await deleteItem({ variables: { id: row.id }});
              snack.success('Time log deleted');
              refetch();
            }
          }

          const onSubmitAdmin = async () => {
            if (window.confirm('Are you sure you want to submit to admin?')) {
              try {
                await updateItems({
                  variables: {
                    ids: data.rows.filter(i => i.status === 'pending').map(i => i.id),
                    data: { status: 'submitted' }
                  }
                });
                snack.success('Time logs submitted for review');
              } catch (e) {
                snack.error(e.message);
              }
            }
          }

          const nextDue = sumBy(data.rows.filter(i => i.status === 'approved'), 'total') / 100;
          const hasPending = data.rows.some(i => i.status === 'pending');

          return (
            <>
              <Box marginBottom={2} display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Button variant="contained" color="primary" onClick={setShowAdd}>
                    Log Time
                  </Button>
                  {' '}
                  {hasPending && (
                    <Button variant="outlined" color="primary" onClick={onSubmitAdmin}>
                      Submit to Admin
                    </Button>
                  )}
                </Box>
                <Typography variant="subtitle1">Next Payout: ${nextDue}</Typography>
              </Box>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell>Student/Group</TableCell>
                      <TableCell align="right">Duration</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Remark</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.rows.map((row) => (
                      <TableRow key={row.name}>
                        <TableCell component="th" scope="row">
                          {row.date}
                        </TableCell>
                        <TableCell>{row.payment_category.label}</TableCell>
                        <TableCell>{row.student ? getFullName(row.student) : row.group?.name}</TableCell>
                        <TableCell align="right">
                          {row.duration} minutes
                        </TableCell>
                        <TableCell align="right">${row.total / 100}</TableCell>
                        <TableCell>{row.status}</TableCell>
                        <TableCell>{row.comments}</TableCell>
                        <TableCell>
                          <IconButton onClick={() => onDelete(row)} color="secondary">
                            <DeleteIcon />
                          </IconButton>
                          <IconButton onClick={() => setEditing(row)}>
                            <EditIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {(showAdd ||editing) && <LogTime onClose={() => {
                  setShowAdd(false);
                  setEditing(null);
                }} item={editing} onDone={refetch} />}
              </TableContainer>
            </>
          );
        }}
      </Query>
    </Box>
  )
}

const Invoices = () => {
  const user = useSelector((s) => s.user.user);
  const [selected, setSelected] = useState();

  return (
    <Query query={GET_USER_INVOICES} variables={{ userId: user.id }}>
      {({ data, loading }) => {
        if (loading) return <Spin spinning />;
        const { rows } = data;

        const columns = [
          { title: 'Created At', field: 'created_at', type: 'date' },
          {
            title: 'Amount',
            field: 'total',
            render: (row) => `$${row.total / 100}`,
            type: 'numeric'
          },
          { title: 'status', field: 'status' },
          { title: 'Paid At', field: 'paid_at', type: 'date' },
          {
            title: 'Status Message',
            field: 'last_attempt_error',
          },
        ];

        return (
          <Box>
            <MaterialTable
              title="Invoices"
              options={{
                search: false,
                pageSize: 10,
              }}
              columns={columns}
              data={rows}
              onRowClick={(e, row) => setSelected(row)}
            />
            { selected && <InvoiceDetails id={selected.id} onClose={() => setSelected()} />}
          </Box>
        );
      }}
    </Query>
  )
}

const TimeTracker = ({ location }) => {
  const user = useSelector((s) => s.user.user);
  const tabs = [
    '/time-tracker',
    '/time-tracker/invoices',
    '/time-tracker/bonus',
  ];

  return (
    <Box>
      <Query query={GET_PAYMENT_ACCOUNT} variables={{ userId: user.id }}>
        {({ data, loading }) => {
          if (!data) return null;
          if (data.payment_accounts.length) {
            if (!data.payment_accounts[0].payouts_enabled) {
              return (
                <Alert color="warning" action={<Button component={Link} to="/profile/payment">Verify Account</Button>}>
                  Your payment account is pending verification.
                </Alert>
              )
            }
            return null;
          } else {
            return (
              <Alert color="error" action={<Button component={Link} to="/profile/payment">Setup Now</Button>}>
                Your have not configured your account to receive payout.
              </Alert>
            )
          }
        }}
      </Query>
      <Box marginBottom={2}>
        <Tabs
          value={location.pathname}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab value={tabs[0]} label="Time logs" component={Link} to={tabs[0]} />
          <Tab value={tabs[1]} label="Invoices" component={Link} to={tabs[1]} />
          <Tab value={tabs[2]} label="Bonus History" component={Link} to={tabs[2]} />
        </Tabs>
      </Box>
      <Switch>
        <Route exact path={tabs[0]} component={TimeLogs} />
        <Route exact path={tabs[1]} component={Invoices} />
        <Route exact path={tabs[2]} render={() => <BonusLogs userId={user.id} />} />
      </Switch>
    </Box>
  );
};

export default TimeTracker;
